import { Stack, Typography } from '@mui/material';
import { PropsWithChildren, useCallback } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { CloseButton } from '@/components/form/baseInputs/Button/CloseButton';
import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { Download01Icon } from '@/components/icons/Download01Icon';
import { useFormContext } from '@/components/react-hook-form';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { DeathOrderSelector } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/header/DeathOrderSelector';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import {
  EntityBalanceSheetPage,
  EntityBalanceSheetSlide,
} from '@/modules/presentation/clientPresentation/components/slides/EntityBalanceSheet/EntityBalanceSheetSlide';
import { useBeneficiariesDispositionsToolbars } from '@/modules/presentation/clientPresentation/components/slides/EntityBundle/EntityBeneficiariesAndDispositionsSlide/useBeneficiariesDispositionsToolbars';
import { useEntityCoverToolbars } from '@/modules/presentation/clientPresentation/components/slides/EntityBundle/EntityCoverSlide/useEntityCoverToolbars';
import { useInsuranceToolbar } from '@/modules/presentation/clientPresentation/components/slides/EntityBundle/EntityInsurancePoliciesSlide/useInsuranceToolbar';
import { PresentationSlideWrapper } from '@/modules/presentation/clientPresentation/PresentationSlideWrapper';
import { EntityBeneficiariesAndDispositionsSlide } from '@/modules/presentation/slides/EntityBeneficiariesAndDispositionsSlide/EntityBeneficiariesAndDispositionsSlide';
import { EntityCoverSlide } from '@/modules/presentation/slides/EntityCoverSlide/EntityCoverSlide';
import { PreviewAwareEntityDiagramAndNotesSlide } from '@/modules/presentation/slides/EntityDiagramAndNotesSlide/PreviewAwareEntityDiagramAndNotesSlide';
import { EntityInsurancePoliciesSlide } from '@/modules/presentation/slides/EntityInsurancePoliciesSlide/EntityInsurancePoliciesSlide';
import { COLORS } from '@/styles/tokens/colors';
import { PathsOf } from '@/types/subform';
import { UnreachableError } from '@/utils/errors';

import { EntitySlideType } from '../ExportEntityBundleModal/ExportEntityBundleModal.types';
import { useGuardedEntityPreviewModalPaginationContext } from './EntityPreviewModal.paginationContext';
import { EntityPreviewModalFormShape } from './EntityPreviewModal.types';
import { EntityPreviewModal_EntityFragment } from './graphql/EntityPreviewModal.generated';

export interface EntityPreviewModalHeaderProps {
  onClose: () => void;
  onExport: () => void;
  exportLoading: boolean;
}

export function EntityPreviewModalHeader({
  onClose,
  onExport,
  exportLoading,
}: EntityPreviewModalHeaderProps) {
  const { control, setValue } = useFormContext<EntityPreviewModalFormShape>();
  const { isTwoClientHousehold } = useHouseholdDetailsContext();
  const { currentSlideType } = useGuardedEntityPreviewModalPaginationContext();
  const [deathOrderId, isTimeBoundEntity, isBeneficiaryDesignationEntity] =
    useWatch({
      control,
      name: [
        'deathOrderId',
        'isTimeBoundEntity',
        'isBeneficiaryDesignationEntity',
      ],
    });

  const handleDeathOrderChange = useCallback(
    (clientId: string) => {
      setValue('deathOrderId', clientId);
    },
    [setValue]
  );

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: COLORS.GRAY[200],
      }}
    >
      <Typography variant="title-l" component="span">
        Preview
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <Stack direction="row" spacing={2} alignItems="center">
          {currentSlideType === EntitySlideType.DiagramAndNotesSlide && (
            <FormAwareSwitch
              fieldName={
                'shouldDisplayNotes' as const satisfies PathsOf<EntityPreviewModalFormShape>
              }
              label="Show presentation notes"
              labelPosition="right"
              control={control}
            />
          )}
          <Stack direction="row" spacing={1} alignItems="center">
            {!isTimeBoundEntity &&
              !isBeneficiaryDesignationEntity &&
              isTwoClientHousehold &&
              deathOrderId && (
                <DeathOrderSelector
                  selectedFirstDeathClientId={deathOrderId}
                  onDeathOrderChange={handleDeathOrderChange}
                />
              )}
            <Button
              variant="primary"
              size="sm"
              onClick={onExport}
              startIcon={Download01Icon}
              loading={exportLoading}
            >
              Export PDF
            </Button>
          </Stack>
        </Stack>
        <CloseButton variant="transparent" size="sm" onClick={onClose} />
      </Stack>
    </Stack>
  );
}

export interface EntityPreviewModalSlideDisplayProps {
  onUpdateNotes: (newNotes: string) => Promise<void>;
  entity: EntityPreviewModal_EntityFragment;
}

export function EntityPreviewModalSlideDisplay({
  entity,
  onUpdateNotes,
}: EntityPreviewModalSlideDisplayProps) {
  const { control } = useFormContext<EntityPreviewModalFormShape>();
  const [shouldDisplayNotes, deathOrderId] = useWatch({
    control,
    name: ['shouldDisplayNotes', 'deathOrderId'],
  });
  const { currentSlideType, currentSlideSubIndex, policyPages, slidesProps } =
    useGuardedEntityPreviewModalPaginationContext();

  // The same edit-mode hover toolbars the presentation designer shows, gated on
  // edit permission. The toolbars are inert without the hover/actions providers
  // this surface mounts around the slide area (see EntityPreviewModal).
  const canEdit = useCanEditHouseholdData();
  const { renderHeaderSection, renderDetailsSection } = useEntityCoverToolbars({
    entity,
    slideId: entity.id,
    canEdit,
  });
  const { currentAccessToolbar, dispositionsToolbar } =
    useBeneficiariesDispositionsToolbars({ entity, canEdit });
  const insuranceToolbar = useInsuranceToolbar({ entity, canEdit });

  switch (currentSlideType) {
    case EntitySlideType.CoverSlide:
      return (
        <EntityCoverSlide
          entity={entity}
          SlideWrapper={EntityPreviewModalPresentationSlideWrapper}
          id={entity.id}
          renderHeaderSection={renderHeaderSection}
          renderDetailsSection={renderDetailsSection}
        />
      );
    case EntitySlideType.DiagramAndNotesSlide:
      return (
        <PreviewAwareEntityDiagramAndNotesSlide
          key={`${entity.id}-${deathOrderId}-${shouldDisplayNotes}`}
          entity={entity}
          slideId={entity.id}
          deathOrderId={deathOrderId}
          onUpdateNotes={onUpdateNotes}
          SlideWrapper={EntityPreviewModalPresentationSlideWrapper}
          hideNotes={!shouldDisplayNotes}
        />
      );
    case EntitySlideType.BalanceSheetSlide:
      return (
        <EntityBalanceSheetSlide
          entityId={entity.id}
          entityName={entity.subtype.displayName}
          slideIdPrefix={`${entity.id}-balance-sheet`}
          SlideWrapper={EntityPreviewModalPresentationSlideWrapper}
          standalone
          renderPage={(props) =>
            props.pageIndex === currentSlideSubIndex ? (
              <EntityBalanceSheetPage key={props.slideId} {...props} />
            ) : null
          }
        />
      );
    case EntitySlideType.BeneficiariesAndDispositionsSlide: {
      const slideProps = slidesProps[currentSlideSubIndex];
      if (!slideProps) {
        return null;
      }
      return (
        <EntityPreviewModalPresentationSlideWrapper
          key={`${entity.id}-${currentSlideSubIndex}`}
        >
          <EntityBeneficiariesAndDispositionsSlide
            slideId={`${entity.id}-${currentSlideSubIndex}`}
            {...slideProps}
            currentAccessToolbar={currentAccessToolbar}
            dispositionsToolbar={dispositionsToolbar}
          />
        </EntityPreviewModalPresentationSlideWrapper>
      );
    }
    case EntitySlideType.InsurancePolicySlides: {
      const pagePolicies = policyPages[currentSlideSubIndex];
      if (!pagePolicies) {
        return null;
      }
      const slideId = `${entity.id}-${currentSlideSubIndex}`;

      return (
        <EntityInsurancePoliciesSlide
          key={slideId}
          pagePolicies={pagePolicies}
          slideId={slideId}
          displayName={entity.subtype.displayName}
          subheading={entity.extendedDisplayKind}
          // Number the page so the preview header matches the exported PDF.
          pageNumber={{
            index: currentSlideSubIndex,
            total: policyPages.length,
          }}
          SlideWrapper={EntityPreviewModalPresentationSlideWrapper}
          toolbar={insuranceToolbar}
        />
      );
    }
    default: {
      throw new UnreachableError({
        case: currentSlideType,
        message: `Unhandled slide type: ${currentSlideType}`,
      });
    }
  }
}

function EntityPreviewModalPresentationSlideWrapper({
  children,
}: PropsWithChildren) {
  return (
    <PresentationSlideWrapper
      sx={{
        maxWidth: '100%',
      }}
    >
      {children}
    </PresentationSlideWrapper>
  );
}
